import classNames from "classnames/bind";
import { isPlainObject } from "es-toolkit/compat";
import React, { useMemo } from "react";
import {
  useController,
  type FieldValues,
  type ValidateResult,
} from "react-hook-form";
import { CalculatedFieldDataDisplay } from "../../DataDisplay/CalculatedFieldDataDisplay";
import { Icon, IconLoading } from "../../Icon";
import { mergeProps } from "../../utils/component-props";
import { DataInputWithAdornment } from "../_components/DataInputWithAdornment";
import { InputWithLabels } from "../_components/InputWithLabels";
import styles from "./CalculatedFieldInput.module.scss";
import type {
  CalculatedFieldInputProps,
  ControlledCalculatedFieldInputProps,
} from "./types";

const cx = classNames.bind(styles);

const CalculatedFieldInput = ({
  name,
  id,
  labelledById,
  value,
  displaySlot,
  invalidMessage,
  "aria-invalid": ariaInvalid,
  error,
  isDisabled = false,
  isBusy = false,
  isLoading = false,
  isReady = false,
  isInline = false,
  isFluid = true,
  placeholder = "Enter required input values to calculate",
  busyText = "Calculating...",
  validText = "Successfully calculated",
  showHint = true,
  hintSlot,
  leftSlot,
  ...restProps
}: CalculatedFieldInputProps) => {
  return (
    <div className={cx("container", { isInline, isFluid })}>
      {leftSlot ? leftSlot({ className: cx("left-slot") }) : null}
      <DataInputWithAdornment
        isBusy={isBusy}
        isLoading={isLoading}
        error={invalidMessage}
        showStatus={false}
        adornmentSlot={({
          TriggerWrapper,
          caretIcon,
          iconProps,
          showDetails,
        }) => (
          <>
            <TriggerWrapper>
              <Icon
                {...iconProps}
                variant="calculator"
                alt="Calculated Field"
                className={cx("trigger-wrapper-icon")}
              />
              <span className={cx("trigger-wrapper-text")}>
                {showDetails ? "Hide" : "Show"} Equation
              </span>
              {caretIcon}
            </TriggerWrapper>
          </>
        )}
        detailsSlot={showHint ? hintSlot : null}
        className={cx("input", {
          "has-label-inside": !!leftSlot,
          "is-inline": isInline,
        })}
      >
        {({ isBusy, isLoading }) => (
          <>
            <input
              readOnly
              hidden
              disabled={isDisabled}
              name={name}
              value={value}
              id={id ?? name}
              aria-labelledby={labelledById}
            />
            {!isReady ? (
              <div className={cx("status-container")}>
                <p className={cx("status")}>
                  {isBusy ? busyText : placeholder}
                </p>
                {isBusy ? (
                  <IconLoading
                    speed="slow"
                    size={16}
                  />
                ) : null}
              </div>
            ) : (
              <CalculatedFieldDataDisplay
                value={value}
                displaySlot={displaySlot}
                showHint={showHint}
                isStatic={false}
                isReady={isReady}
                isBusy={isBusy}
                isLoading={isLoading}
                aria-invalid={ariaInvalid}
                validText={validText}
                busyText={busyText}
                placeholder={placeholder}
                {...restProps}
              />
            )}
          </>
        )}
      </DataInputWithAdornment>
    </div>
  );
};

CalculatedFieldInput.displayName = "CalculatedFieldInput";

export const ControlledCalculatedFieldInput = <T extends FieldValues>({
  name,
  defaultValue,
  rules,
  label,
  isLabelShown,
  labelPosition,
  description,
  isRequired,
  isOptionalTextShown,
  shouldShowDescriptionInTooltip,
  isInline = false,
  isFluid = true,
  ...restProps
}: ControlledCalculatedFieldInputProps<T>) => {
  const { isDisabled, isLoading, isBusy } = restProps;
  const { field, fieldState } = useController({
    name,
    defaultValue,
    rules: {
      ...rules,
      validate:
        rules?.validate && typeof rules.validate === "function"
          ? rules.validate
          : {
              ...(isRequired
                ? {
                    isRequired: (value: string): ValidateResult =>
                      [null, undefined, ""].includes(value)
                        ? `${typeof label === "string" ? label : "Calculated Field input"} is required`
                        : undefined,
                  }
                : {}),
              ...(rules?.validate && isPlainObject(rules.validate)
                ? rules.validate
                : {}),
            },
    },
  });

  const mergedProps = useMemo(() => {
    return mergeProps(restProps, {
      onChange: field.onChange,
      onBlur: field.onBlur,
    });
  }, [restProps, field.onChange, field.onBlur]);

  const invalidMessage = fieldState.invalid
    ? fieldState.error?.message ||
      `Input is invalid (${fieldState.error?.type})`
    : undefined;

  return (
    <InputWithLabels
      colorScheme="light"
      isDisabled={isDisabled ?? false}
      isLoading={isLoading ?? false}
      isBusy={isBusy ?? false}
      label={label}
      isLabelShown={isLabelShown}
      labelPosition={labelPosition}
      description={description}
      isRequired={isRequired}
      isOptionalTextShown={isOptionalTextShown}
      shouldShowDescriptionInTooltip={shouldShowDescriptionInTooltip}
      isInline={isInline}
      isFluid={isFluid}
      invalidMessage={invalidMessage}
    >
      {({ invalidMessageId, labelId, inputId, flairSlot, leftSlot }) => (
        <CalculatedFieldInput
          id={inputId}
          labelledById={labelId}
          error={fieldState.error}
          invalidMessage={invalidMessage}
          aria-invalid={fieldState.invalid}
          aria-describedby={invalidMessageId}
          {...(!isLabelShown && !description && typeof label === "string"
            ? { "aria-label": label }
            : {})}
          invalidText={flairSlot ? invalidMessage : undefined}
          leftSlot={leftSlot}
          isInline={isInline}
          isFluid={isFluid}
          {...field}
          {...mergedProps}
        />
      )}
    </InputWithLabels>
  );
};

ControlledCalculatedFieldInput.displayName = "ControlledCalculatedFieldInput";

export default CalculatedFieldInput;
